import { computed } from 'vue'
import { useTemplateStore } from '@/stores/template'
import Swal from 'sweetalert2'

export function useAlert() {
  const store = useTemplateStore()

  const isDarkMode = computed(() => store.settings.darkMode)

  // Configurações base para todos os alerts
  const baseConfig = computed(() => ({
    customClass: {
      popup: isDarkMode.value ? 'swal2-dark' : '',
      confirmButton: 'btn btn-primary m-1',
      cancelButton: 'btn btn-alt-secondary m-1',
      actions: 'swal2-actions-custom'
    },
    background: isDarkMode.value ? '#2c3338' : '#fff',
    color: isDarkMode.value ? '#e4e6e7' : '#343a40',
    buttonsStyling: false
  }))

  // Toast notifications
  const showToast = (message, type = 'success', customConfig = {}) => {
    const config = {
      ...baseConfig.value,
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      icon: type,
      title: message,
      ...customConfig
    }

    return Swal.fire(config)
  }

  // Confirmation dialogs
  const showConfirm = ({
    title = 'Are you sure?',
    text = 'This action cannot be undone.',
    icon = 'warning',
    confirmButtonText = 'Yes',
    cancelButtonText = 'Cancel',
    confirmButtonClass,
    cancelButtonClass = 'btn-alt-secondary',
    isDelete = false,
    customConfig = {}
  } = {}) => {
    // Se for um alert de deleção ou se o texto contiver palavras-chave de deleção
    const isDeleteAlert =
      isDelete ||
      text.toLowerCase().includes('delete') ||
      confirmButtonText.toLowerCase().includes('delete')

    // Define a classe do botão de confirmação baseado no tipo de alert
    const defaultConfirmClass = isDeleteAlert ? 'btn-danger' : 'btn-primary'
    const finalConfirmClass = confirmButtonClass || defaultConfirmClass

    const config = {
      ...baseConfig.value,
      title,
      text,
      icon,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        ...baseConfig.value.customClass,
        confirmButton: `btn ${finalConfirmClass} m-1`,
        cancelButton: `btn ${cancelButtonClass} m-1`
      },
      ...customConfig
    }

    return Swal.fire(config)
  }

  // Simple alerts
  const showAlert = (title, text = '', icon = 'info', customConfig = {}) => {
    const config = {
      ...baseConfig.value,
      title,
      text,
      icon,
      ...customConfig
    }

    return Swal.fire(config)
  }

  // Error alerts
  const showError = (
    title = 'Error',
    text = 'Something went wrong.',
    customConfig = {}
  ) => {
    return showAlert(title, text, 'error', customConfig)
  }

  // Success alerts
  const showSuccess = (title = 'Success', text = '', customConfig = {}) => {
    return showAlert(title, text, 'success', customConfig)
  }

  return {
    showToast,
    showConfirm,
    showAlert,
    showError,
    showSuccess
  }
}
